<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { useBotsStore } from '@/stores/bots';
import { useAuthStore } from '@/stores/auth';
import { useIntentsStore } from '@/stores/intents';
import { useFormsStore } from '@/stores/forms';
import { useContentStore } from '@/stores/content';
import { useKnowledgeBaseStore } from '@/stores/knowledgeBase';
import { usePreferencesStore } from '@/stores/preferences';
import { useRoute } from 'vue-router';
import AppBotSelect from '@/components/app/AppBotSelect.vue';
import HubLoader from '@/components/hub/HubLoader.vue';

/**
 * Root-level bootstrapper: loads the user's bots, resolves which bot to open
 * (query param / last used / single bot) and hydrates its data before revealing
 * the app. Shows a bot picker when the choice is ambiguous.
 */
const emit = defineEmits<{
  (e: 'onBotLoaded'): void;
}>();

const botsStore = useBotsStore();
const authStore = useAuthStore();
const intentsStore = useIntentsStore();
const formsStore = useFormsStore();
const contentStore = useContentStore();
const knowledgeBaseStore = useKnowledgeBaseStore();
const preferencesStore = usePreferencesStore();
const route = useRoute();

const isLoading = ref(true);
const isBotSelectShown = ref(false);

const bots = computed<any[]>(() => botsStore.botsGetter);
const currentBot = computed<any>(() => botsStore.currentBotGetter);
const currentBotId = computed<string>(() => botsStore.currentBotId);

async function initBots() {
  await botsStore.clear();
  await botsStore.get();

  if (!bots.value.length) {
    console.error('No bots found');
    // logout and go to login page
    authStore.logout();
    return;
  }

  // User has more bots
  if (bots.value.length > 1) {
    const { bid } = route.query as { bid?: string };
    const lastUsedBotId = authStore.lastUsedBotIdGetter;

    let botId = bid || lastUsedBotId || null;

    if (route.name === 'preview') {
      botId = route.params.botId as string;
    }

    if (botId) {
      loadBot(botId);
      return;
    }
    isBotSelectShown.value = true;
    await new Promise((resolve) => setTimeout(resolve, 300));
    isLoading.value = false;
    return;
  }
  // User has 1 bot
  else if (bots.value.length === 1) {
    await loadBot(bots.value[0].uniqueBotId);
  }
}

async function loadData() {
  // 🚀 Load data for bot (intents, forms ...)
  const botId1 = currentBot.value.stagingBot;
  const botId2 = currentBot.value.uniqueBotId;
  const botId = currentBotId.value;
  await botsStore.getBotById(botId2);

  // Run all requests in parallel
  await Promise.all([
    botsStore.getBotById(botId1),
    intentsStore.get(botId),
    formsStore.get(botId),
    contentStore.getDeployment({ botId }),
  ]);

  // Background loading
  Promise.allSettled([
    knowledgeBaseStore.fetchResources({ botId }),
  ]).then((results) => {
    for (const result of results) {
      if (result.status === 'fulfilled') continue;
      console.error(result.reason);
    }
  });

  // Select channel based on query & last used channel
  const chId = preferencesStore.suggestedChannelId;
  botsStore.setCurrentChannelId(chId);
}

async function loadBot(botId: string) {
  isBotSelectShown.value = false;

  const bot = bots.value.find((b) => {
    if (b.uniqueBotId === botId) return true;
    if (b.stagingBot === botId) return true;
    return false;
  });

  if (!bot) {
    console.error(`No Bot with id "${botId}" found`);
    if (bots.value.length > 1) {
      isBotSelectShown.value = true;
    }
    return;
  }

  botsStore.setCurrentBot(bot);

  if (!currentBotId.value) {
    console.error('No currentBotId found');
    return;
  }
  // 🚀 Load data for bot (intents, forms ...)
  await loadData();

  authStore.setLastUsedBotId(currentBotId.value);

  await nextTick();

  emit('onBotLoaded');

  // Wait to prevent flickering
  await new Promise((resolve) => setTimeout(resolve, 300));
  isLoading.value = false;
}

initBots();
</script>

<template>
  <div>
    <Transition name="fade">
      <div v-if="isBotSelectShown">
        <AppBotSelect @onBotChange="loadBot($event)" />
      </div>
    </Transition>
    <Transition name="fade">
      <div v-if="isLoading">
        <HubLoader fixed />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
